import type { CustomerProfile } from "@station/contracts";
import { Alert, SkeletonRows, Tabs, TabsContent, TabsList, TabsTrigger } from "@station/ui";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import type { ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { ConsentsTab } from "../../../features/customers/consents-tab.js";
import { CustomerActions } from "../../../features/customers/customer-actions.js";
import { DEFAULT_OVERVIEW_PERIOD, OVERVIEW_PERIODS } from "../../../features/customers/customer-values.js";
import { LinkTab } from "../../../features/customers/link-tab.js";
import { CustomerLoyaltyTab } from "../../../features/loyalty/customer-loyalty-tab.js";
import { useLoyaltyPermissions } from "../../../features/loyalty/queries.js";
import { OverviewTab } from "../../../features/customers/overview-tab.js";
import { MaskedNotice, PersonalValue } from "../../../features/customers/personal-data.js";
import { formatPhoneDisplay } from "../../../features/customers/personal-data-values.js";
import { useCustomer, useCustomerPermissions } from "../../../features/customers/queries.js";
import {
  CustomersPage,
  CustomerStatusBadge,
  DriverLinkBadge,
  Field,
  useCustomersCrumb,
} from "../../../features/customers/shared.js";
import { VehiclesTab } from "../../../features/customers/vehicles-tab.js";
import { errorMessage } from "../../../lib/errors.js";
import { formatDateTime } from "../../../lib/format.js";

const TABS = ["overview", "loyalty", "vehicles", "consents", "link"] as const;
type TabKey = (typeof TABS)[number];

const searchSchema = z.object({
  tab: z.enum(TABS).optional().catch(undefined),
  period: z.enum(OVERVIEW_PERIODS).optional().catch(undefined),
});

export const Route = createFileRoute("/_app/musteriler/$id")({
  validateSearch: (search) => searchSchema.parse(search),
  component: CustomerDetailRoute,
});

/**
 * Müşteri ayrıntısı (Prompt 17 web): başlık (kod · ad, durum, kart no) + eylemler; sekmeler müşteri 360, sadakat (puan
 * hesabı, defter, düzeltme, kupon, abonelik — `loyalty.read` ister), araçlar, rıza / ileti izinleri ve müşteri uygulaması
 * bağlantısı (`?tab=`; sekme içeriği yalnızca seçiliyken çizilir).
 */
function CustomerDetailRoute(): ReactElement {
  const { t } = useTranslation();
  const { id } = Route.useParams();
  const crumb = useCustomersCrumb();
  const permissions = useCustomerPermissions();
  const customer = useCustomer(id, permissions.enabled && permissions.canRead);
  const data = customer.data;
  const title = data
    ? data.personalDataMasked || data.fullName === null
      ? data.code
      : `${data.code} · ${data.fullName}`
    : t("customers.detail.title");

  return (
    <CustomersPage
      title={title}
      breadcrumb={[crumb, { label: data?.code ?? "…" }]}
      actions={data && permissions.canManage ? <CustomerActions customer={data} /> : null}
    >
      {customer.error ? (
        <Alert tone="danger">{errorMessage(customer.error)}</Alert>
      ) : !data ? (
        <SkeletonRows rows={8} />
      ) : (
        <CustomerDetail customer={data} />
      )}
    </CustomersPage>
  );
}

function CustomerDetail({ customer }: { customer: CustomerProfile }): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate({ from: Route.fullPath });
  const search = Route.useSearch();
  const permissions = useCustomerPermissions();
  const loyalty = useLoyaltyPermissions();
  const tabs = TABS.filter((key) => key !== "loyalty" || loyalty.canRead);
  const tab: TabKey = search.tab && tabs.includes(search.tab) ? search.tab : "overview";
  const period = search.period ?? DEFAULT_OVERVIEW_PERIOD;

  return (
    <div className="grid min-w-0 gap-4">
      <MaskedNotice masked={customer.personalDataMasked} />
      {customer.status === "ANONYMIZED" ? (
        <Alert tone="warning" title={t("customers.detail.anonymizedTitle")}>
          {t("customers.detail.anonymizedDescription", { at: formatDateTime(customer.anonymizedAt) })}
        </Alert>
      ) : null}
      <dl className="grid gap-x-6 gap-y-3 rounded-lg border bg-card p-4 sm:grid-cols-2 lg:grid-cols-4">
        <Field label={t("customers.fields.fullName")}>
          <PersonalValue value={customer.fullName} masked={customer.personalDataMasked} />
        </Field>
        <Field label={t("common.status")}>
          <CustomerStatusBadge status={customer.status} />
        </Field>
        <Field label={t("customers.fields.cardNo")}>
          {customer.loyaltyCardNo ? (
            <span className="font-mono">{customer.loyaltyCardNo}</span>
          ) : (
            <span className="text-muted-foreground">{t("customers.detail.noCard")}</span>
          )}
        </Field>
        <Field label={t("customers.fields.driverApp")}>
          <DriverLinkBadge linked={customer.driverLinked} />
        </Field>
        <Field label={t("customers.fields.phone")}>
          <PersonalValue
            value={customer.phone}
            masked={customer.personalDataMasked}
            format={formatPhoneDisplay}
          />
        </Field>
        <Field label={t("customers.fields.email")}>
          <PersonalValue value={customer.email} masked={customer.personalDataMasked} className="break-all" />
        </Field>
        <Field label={t("customers.fields.homeStation")}>{customer.homeStationName ?? "—"}</Field>
        <Field label={t("customers.fields.counterparty")}>{customer.counterpartyName ?? "—"}</Field>
        {customer.notes ? (
          <div className="sm:col-span-2 lg:col-span-4">
            <Field label={t("customers.fields.notes")}>
              <PersonalValue value={customer.notes} masked={customer.personalDataMasked} />
            </Field>
          </div>
        ) : null}
      </dl>

      <Tabs
        value={tab}
        onValueChange={(next) =>
          void navigate({
            search: (previous) => ({ ...previous, tab: next === "overview" ? undefined : (next as TabKey) }),
            replace: true,
          })
        }
      >
        <TabsList aria-label={t("customers.detail.tabsLabel")} className="flex-wrap">
          {tabs.map((key) => (
            <TabsTrigger key={key} value={key}>
              {t(`customers.tabs.${key}`)}
            </TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value="overview">
          {tab === "overview" ? (
            <OverviewTab
              customerId={customer.id}
              period={period}
              onPeriodChange={(next) =>
                void navigate({
                  search: (previous) => ({
                    ...previous,
                    period: next === DEFAULT_OVERVIEW_PERIOD ? undefined : next,
                  }),
                  replace: true,
                })
              }
            />
          ) : null}
        </TabsContent>
        <TabsContent value="loyalty">
          {tab === "loyalty" ? <CustomerLoyaltyTab customer={customer} /> : null}
        </TabsContent>
        <TabsContent value="vehicles">
          {tab === "vehicles" ? <VehiclesTab customer={customer} canManage={permissions.canManage} /> : null}
        </TabsContent>
        <TabsContent value="consents">
          {tab === "consents" ? <ConsentsTab customer={customer} permissions={permissions} /> : null}
        </TabsContent>
        <TabsContent value="link">
          {tab === "link" ? <LinkTab customer={customer} canManage={permissions.canManage} /> : null}
        </TabsContent>
      </Tabs>
    </div>
  );
}
